// NOTE: NSAI-7215 — the Intelligent UI contract (```ui fences).
//
// The SPEC (`IUiSpec`) is what the assistant writes: a short list of native
// components plus, optionally, inputs and the formulas that derive values from
// them. The model writes formulas, never results — the device computes, for
// the same reason the diagram spec carries no geometry: a one-shot model is bad
// at arithmetic it cannot check, and a wrong total looks exactly as confident
// as a right one.
//
// Nothing in a spec is code. Expressions are parsed by the bounded grammar in
// lib/ui/expression.ts, which reaches only the spec's own inputs and values.

import type { TChartData, TChartValueFormat } from "./chart";
import type { TDiagramSpec } from "./diagram";
import type { IMapSpec } from "./map";

export enum EUiInputKind {
  SLIDER = "slider",
  NUMBER = "number",
  SELECT = "select",
  TOGGLE = "toggle",
}

export enum EUiBlockType {
  STATS = "stats",
  COMPARISON = "comparison",
  TABLE = "table",
  STEPS = "steps",
  TABS = "tabs",
  ACTIONS = "actions",
  PLOT = "plot",
  CHART = "chart",
  DIAGRAM = "diagram",
  MAP = "map",
}

export enum EUiPlotKind {
  LINE = "line",
  AREA = "area",
  BAR = "bar",
}

export enum EUiTableAlign {
  START = "start",
  END = "end",
}

// NOTE: How a computed number is shown — the same formats a chart value takes,
// so a stat tile and the plot beside it read alike.
export type TUiValueFormat = TChartValueFormat;

export type TUiScalar = number | boolean;

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

interface IUiInputBase {
  id: string;
  label: string;
}

export interface IUiSliderInput extends IUiInputBase {
  kind: EUiInputKind.SLIDER;
  min: number;
  max: number;
  value: number;
  step?: number;
  unit?: string;
}

export interface IUiNumberInput extends IUiInputBase {
  kind: EUiInputKind.NUMBER;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
}

export interface IUiSelectOption {
  label: string;
  value: number;
}

export interface IUiSelectInput extends IUiInputBase {
  kind: EUiInputKind.SELECT;
  options: IUiSelectOption[];
  value: number;
}

export interface IUiToggleInput extends IUiInputBase {
  kind: EUiInputKind.TOGGLE;
  value: boolean;
}

export type TUiInput =
  | IUiSliderInput
  | IUiNumberInput
  | IUiSelectInput
  | IUiToggleInput;

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

export interface IUiStatsItem {
  label: string;
  // NOTE: An expression over the spec's inputs and values, or a literal.
  value: string;
  format?: TUiValueFormat;
  hint?: string;
}

export interface IUiStatsBlock {
  type: EUiBlockType.STATS;
  items: IUiStatsItem[];
  title?: string;
}

export interface IUiComparisonOption {
  id: string;
  label: string;
  sublabel?: string;
}

export interface IUiComparisonAttribute {
  label: string;
  // NOTE: One cell per option, in the order the options are listed.
  values: string[];
}

export interface IUiComparisonBlock {
  type: EUiBlockType.COMPARISON;
  options: IUiComparisonOption[];
  attributes: IUiComparisonAttribute[];
  title?: string;
  recommended?: string;
  verdict?: string;
}

export interface IUiTableColumn {
  label: string;
  align?: EUiTableAlign;
}

export interface IUiTableBlock {
  type: EUiBlockType.TABLE;
  columns: IUiTableColumn[];
  rows: (string | number)[][];
  title?: string;
}

export interface IUiStep {
  label: string;
  detail?: string;
}

export interface IUiStepsBlock {
  type: EUiBlockType.STEPS;
  items: IUiStep[];
  title?: string;
}

export interface IUiAction {
  label: string;
  // NOTE: Sent as an ordinary user message when pressed. `{id}` placeholders
  // are filled from the current inputs and values.
  prompt: string;
}

export interface IUiActionsBlock {
  type: EUiBlockType.ACTIONS;
  items: IUiAction[];
}

export interface IUiPlotSeries {
  label: string;
  // NOTE: An expression of `x` (and of the spec's inputs and values).
  y: string;
}

export interface IUiPlotBlock {
  type: EUiBlockType.PLOT;
  kind: EUiPlotKind;
  // NOTE: Expressions, so a plot can run "from 1 to years".
  from: string;
  to: string;
  series: IUiPlotSeries[];
  title?: string;
  subtitle?: string;
  step?: number;
  xLabel?: string;
  format?: TUiValueFormat;
}

export interface IUiChartBlock {
  type: EUiBlockType.CHART;
  spec: TChartData;
}

export interface IUiDiagramBlock {
  type: EUiBlockType.DIAGRAM;
  spec: TDiagramSpec;
}

export interface IUiMapBlock {
  type: EUiBlockType.MAP;
  spec: IMapSpec;
}

// NOTE: Everything a tab may hold — every block except another set of tabs,
// which keeps nesting one level deep.
export type TUiLeafBlock =
  | IUiStatsBlock
  | IUiComparisonBlock
  | IUiTableBlock
  | IUiStepsBlock
  | IUiActionsBlock
  | IUiPlotBlock
  | IUiChartBlock
  | IUiDiagramBlock
  | IUiMapBlock;

export interface IUiTab {
  label: string;
  blocks: TUiLeafBlock[];
}

export interface IUiTabsBlock {
  type: EUiBlockType.TABS;
  tabs: IUiTab[];
}

export type TUiBlock = TUiLeafBlock | IUiTabsBlock;

export interface IUiSpec {
  blocks: TUiBlock[];
  title?: string;
  inputs?: TUiInput[];
  // NOTE: Derived values: id → expression. Evaluated in dependency order.
  values?: Record<string, string>;
}

// NOTE: NSAI-7852 — why a block could not be drawn, as an enumerable value
// analytics can group by: the text is not JSON at all, or it is JSON the
// contract refuses.
export enum EUiParseFailure {
  INVALID_JSON = "invalid_json",
  INVALID_SPEC = "invalid_spec",
}

export interface IShortenUiTextProps {
  text: string;
  maxLength: number;
}

// NOTE: `text` is what fits on the card; `fullText` is what the model wrote,
// offered behind an info icon when `isShortened`.
export interface IUiDisplayText {
  text: string;
  fullText: string;
  isShortened: boolean;
}

// NOTE: `canClose` lets the repair add missing closing brackets — only for a
// block that has finished streaming.
export interface IRepairUiJsonProps {
  rawJson: string;
  canClose?: boolean;
}

export type TUiRecord = Record<string, unknown>;

// NOTE: One option row of a comparison in the model's own layout, read into
// its name and its attribute cells in order.
export interface IUiComparisonRow {
  label: string;
  cells: [string, string][];
  sublabel?: string;
  isRecommended: boolean;
  // NOTE: The row's own `id`, kept only to match a block-level `recommended`.
  sourceId?: string;
}

export interface IFindUiRecommendedIndexProps {
  options: IUiComparisonRow[];
  recommended: unknown;
}

export interface IReadUiComparisonRowProps {
  row: unknown;
  columns: string[] | null;
}

export interface IIsUsableUiIdProps {
  id: string;
  taken: ReadonlySet<string>;
}

export interface IZipUiComparisonCellsProps {
  labels: string[];
  values: unknown[];
}

// NOTE: `isRepaired` marks a block drawn only after the client fixed what the
// model wrote: a bracket or a trailing comma (`repairUiJson`), or a comparison
// in its own layout (`reshapeUiComparisons`).
export type TParseUiResult =
  | { success: true; data: IUiSpec; isRepaired?: boolean }
  | { success: false; error: string; failure: EUiParseFailure };

// ---------------------------------------------------------------------------
// Expressions
// ---------------------------------------------------------------------------

export enum EUiExpressionNodeKind {
  NUMBER = "number",
  BOOLEAN = "boolean",
  IDENTIFIER = "identifier",
  UNARY = "unary",
  BINARY = "binary",
  CONDITIONAL = "conditional",
  CALL = "call",
}

export type TUiUnaryOperator = "-" | "+" | "!";

export type TUiBinaryOperator =
  | "+"
  | "-"
  | "*"
  | "/"
  | "%"
  | "^"
  | "=="
  | "!="
  | "<"
  | "<="
  | ">"
  | ">="
  | "&&"
  | "||";

export interface IUiNumberNode {
  kind: EUiExpressionNodeKind.NUMBER;
  value: number;
}

export interface IUiBooleanNode {
  kind: EUiExpressionNodeKind.BOOLEAN;
  value: boolean;
}

export interface IUiIdentifierNode {
  kind: EUiExpressionNodeKind.IDENTIFIER;
  name: string;
}

export interface IUiUnaryNode {
  kind: EUiExpressionNodeKind.UNARY;
  operator: TUiUnaryOperator;
  operand: TUiExpressionNode;
}

export interface IUiBinaryNode {
  kind: EUiExpressionNodeKind.BINARY;
  operator: TUiBinaryOperator;
  left: TUiExpressionNode;
  right: TUiExpressionNode;
}

export interface IUiConditionalNode {
  kind: EUiExpressionNodeKind.CONDITIONAL;
  condition: TUiExpressionNode;
  whenTrue: TUiExpressionNode;
  whenFalse: TUiExpressionNode;
}

export interface IUiCallNode {
  kind: EUiExpressionNodeKind.CALL;
  name: string;
  args: TUiExpressionNode[];
}

export type TUiExpressionNode =
  | IUiNumberNode
  | IUiBooleanNode
  | IUiIdentifierNode
  | IUiUnaryNode
  | IUiBinaryNode
  | IUiConditionalNode
  | IUiCallNode;

export type TParseUiExpressionResult =
  | { success: true; node: TUiExpressionNode; identifiers: string[] }
  | { success: false; error: string };

export interface IEvaluateUiExpressionProps {
  node: TUiExpressionNode;
  scope: ReadonlyMap<string, TUiScalar>;
}

export type TUiInputState = ReadonlyMap<string, TUiScalar>;

export interface IResolveUiScopeProps {
  spec: IUiSpec;
  // NOTE: What the user has changed. An input missing here takes the value
  // the spec gave it.
  inputState?: TUiInputState;
}

export interface IResolveUiValueProps {
  expression: string;
  scope: ReadonlyMap<string, TUiScalar>;
}

export interface IClampUiNumberProps {
  value: number;
  min?: number;
  max?: number;
  // NOTE: Used when `value` is not a finite number (an empty or non-numeric
  // draft); without it such a value is returned as is.
  fallback?: number;
}

export interface IResolveUiSliderStepProps {
  min: number;
  max: number;
  step?: number;
}

export interface ISnapUiSliderValueProps {
  value: number;
  min: number;
  max: number;
  step: number;
}

export interface IFormatUiNumberProps {
  value: number;
  options: Intl.NumberFormatOptions;
  locale?: string;
}

export interface IFormatUiValueProps {
  value: TUiScalar | null;
  format?: TUiValueFormat;
  locale?: string;
}

export interface IFillUiActionPromptProps {
  prompt: string;
  scope: ReadonlyMap<string, TUiScalar>;
  locale?: string;
}

export interface IUiPlotPoint {
  x: number;
  values: (number | null)[];
}

export interface IBuildUiPlotChartProps {
  block: IUiPlotBlock;
  scope: ReadonlyMap<string, TUiScalar>;
  locale?: string;
}

export interface IOrderUiValuesProps {
  values: Record<string, string>;
  // NOTE: Ids a formula may read besides the values themselves: the inputs.
  inputIds: ReadonlySet<string>;
}

export type TOrderUiValuesResult =
  | { success: true; order: string[] }
  | { success: false; error: string };

export interface IWithUiPlotVariableProps {
  scope: ReadonlyMap<string, TUiScalar>;
  xValue: number;
}

export type TUiTableCell = string | number;

export interface ISortUiTableRowsProps {
  rows: TUiTableCell[][];
  columnIndex: number;
  isDescending: boolean;
  locale?: string;
}

export interface IUiTableSort {
  columnIndex: number;
  isDescending: boolean;
}

export interface ICycleUiTableSortProps {
  previous: IUiTableSort | null;
  columnIndex: number;
}

export interface IBuildUiTableRowsProps {
  rows: TUiTableCell[][];
  query: string;
  sort: IUiTableSort | null;
  locale?: string;
}

export interface IFilterUiTableRowsProps {
  rows: TUiTableCell[][];
  query: string;
  locale?: string;
}
